import React, {useState, useEffect} from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

import LoanSlider from '../form/LoanSlider';
import Installments from '../form/Installments';
import FormFooter from '../common/FormFooter';
import Spinner from '../common/Spinner';

import {useDataProviderState, useDataProviderActions} from '../../providers/DataProvider';
import {formSteps} from '../../constants/form';
import {getLoanDefaultOptions, getLoanOptions, postLoanOptions} from '../../api/api';
import useScrollTop from '../../hooks/useScrollTop';
import HighlightBox from "../common/HighlightBox.jsx";
import Grid from "@mui/material/Grid";
import {TrackGoogleAnalyticsEvent} from "../../google-analytics/gaTracker.js";
import {TrackMetaEvent} from "../../meta/pixelEvents.js";

const viewOptions = {
    SALARY: 'sueldo',
    ID: 'ci'
};

export default function LoanOptionsStep() {

    const {uid, loading, data, userData} = useDataProviderState();
    const {nextStep, saveData, setLoading, setErrorMessage} = useDataProviderActions();

    const [view, setView] = useState(viewOptions.SALARY);

    const [formData, setFormData] = useState(data[formSteps.LOAN_OPTIONS]);

    const [loanOptions, setLoanOptions] = useState([]);
    const [loanDefaultData, setLoanDefaultData] = useState({sueldo: null, ci: null});

    useScrollTop();

    TrackGoogleAnalyticsEvent("LoanOptionsStep");
    TrackMetaEvent("Comprar");

    useEffect(() => {
        async function loadDefaultOptions() {
            setLoading(true);

            const response = await getLoanDefaultOptions(uid);
            const {sueldo, ci} = response.data;

            setLoanDefaultData({sueldo, ci});
            setLoanOptions(sueldo.loan);
            setFormData({
                ...formData,
                solicitudTipo: sueldo.loan.solicitudTipo,
                financialProducto: sueldo.loan.productoId,
                monto: sueldo.loan.monto
            });
            setLoading(false);
        }

        loadDefaultOptions();
    }, []);

    async function loadOptions(amount) {
        setLoading(true);

        const response = await getLoanOptions(uid, amount, view);

        setLoanOptions(response.data);
        setLoading(false);
    }

    function handleInstallmentSelectionChange(option) {
        setFormData({
            ...formData,
            cantidadCuotas: option.cantCuotas,
            valorCuota: option.valorCuota,
            primerVto: option.primerVto,
        });
    }

    function handleLoanValueChange(event, loanValue) {
        loadOptions(loanValue);

        setFormData({
            ...formData,
            monto: loanValue,
            cantidadCuotas: 0,
            valorCuota: 0,
            primerVto: 0,
        });
    }

    async function handleNext() {
        setLoading(true);
        const response = await postLoanOptions(formData, uid);

        if (response?.success) {
            saveData(formData, formSteps.LOAN_OPTIONS);
            history.pushState(null, null, `#${response.data.uid}`);
            nextStep();
        } else {
            setErrorMessage();
        }

        setLoading(false);
    }

    function changeView() {
        if (view === viewOptions.SALARY) {
            setView(viewOptions.ID);
            setLoanOptions(loanDefaultData.ci.loan);
        } else {
            setView(viewOptions.SALARY);
            setLoanOptions(loanDefaultData.sueldo.loan);
        }
    }

    return (
        <Container component="section" maxWidth="sm" sx={{background: '#f5f5f5'}}>
            <Box sx={{pb: 2}}>
                <HighlightBox>
                    <Box sx={{display: 'flex'}}>
                        <Box sx={{py: 2}}>
                            <Box className="color-primary" sx={{pl: 3, pr: 3, pb: 1, fontSize: '1.3rem'}}>
                                <b>{userData.nombre},</b>
                            </Box>
                            <Box as="span" sx={{
                                display: 'inline-block',
                                px: 2,
                                color: 'white',
                                background: 'var(--color-primary)',
                                borderBottomRightRadius: '15px',
                                borderTopRightRadius: '15px',
                                fontSize: '1.2rem'
                            }}>
                                {/*Podés solicitar tú préstamo {(view === viewOptions.SALARY) ? 'recibo de sueldo' : 'cédula de identidad'} presentando:*/}
                                Podés solicitar tú préstamo presentando:
                            </Box>
                            <Box sx={{p: 0}}>
                                <ul className="checklist checklist_no_padding">
                                    <li className="checklist__item">Cédula de identidad</li>
                                    {(view === viewOptions.SALARY) ? (
                                        <li className="checklist__item">Recibo de sueldo</li>
                                    ) : null}
                                    {(view === viewOptions.SALARY) ? (
                                        <li className="checklist__item">Constancia de domicilio</li>
                                    ) : null}
                                </ul>
                            </Box>
                        </Box>
                    </Box>
                </HighlightBox>
            </Box>

            <Box as="h3" sx={{mt: 2, marginBottom: '70px'}}>
                Elegí el valor del préstamo que necesitás
            </Box>

            <LoanSlider
                defaultValue={loanDefaultData[view]?.loanDefault}
                step={loanDefaultData[view]?.stepAmount}
                min={loanDefaultData[view]?.loanMin}
                max={loanDefaultData[view]?.loanMax}
                onChange={handleLoanValueChange}
            />

            <Installments
                selected={formData.cantidadCuotas}
                options={loanOptions.cuotas}
                changeHandler={handleInstallmentSelectionChange}
            />

            <FormFooter
                hideBackButton
                onNext={handleNext}
                nextButtonDisabled={!formData.cantidadCuotas}
            />

            {loanDefaultData.ci ? (<Box sx={{textAlign: 'center'}}>
                <Button
                    onClick={changeView}
                    sx={{
                        textTransform: 'inherit',
                        fontWeight: 'bold',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        background: '#afafaf',
                        color: 'white',
                        padding: '2px 10px',
                        '&:hover': {
                            background: '#939191',
                        }
                    }}>
                    {(view === viewOptions.SALARY) ?
                        '¿Querés solicitar tu préstamo a solo cédula?' :
                        '¿Querés solicitar tu préstamo con recibo de sueldo?'
                    }
                </Button>
            </Box>) : null}

            <Spinner loading={loading}/>
        </Container>
    )

}
